/**
 * Community product page (`/shop/product/:id`).
 *
 * Shows one product with the shop that sells it, lets the visitor pick the
 * quantity (and a variation, when the owner created some) and adds it to **that
 * shop's** cart — every cart line remembers its `shop_id` (`CartContext`).
 *
 * The Share button hands out `{origin}/share/product/<id>`: the Open Graph page
 * built by the API, so the preview the visitor sends shows the **product
 * picture** as its thumbnail (+ the price and the shop) — see
 * `Backend_API/routers/share.py`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiArrowLeft, FiChevronLeft, FiChevronRight, FiMinus, FiPlus, FiShare2,
  FiShoppingBag, FiShoppingCart, FiTag,
} from 'react-icons/fi';
import { cProduct, fullUrl } from '../../api';
import { useCart } from '../../contexts/CartContext';
import { useBuy } from '../../contexts/BuyContext';
import { useCustomer } from '../../contexts/CustomerContext';
import { useLanguage } from '../../i18n';
import ShopProductCard from '../../components/community/ShopProductCard';
import ShopSocialLinks from '../../components/community/ShopSocialLinks';
import { BLUE } from '../../components/community/brand';

export default function ShopProductPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const { addItem } = useCart();
  const { askBuy } = useBuy();
  const { customer, isLoggedIn } = useCustomer();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(0);      // index in product.variations

  useEffect(() => {
    let alive = true;
    setLoading(true);
    cProduct(id)
      .then((res) => { if (alive) setProduct(res); })
      .catch(() => { if (alive) setProduct(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const images = useMemo(() => {
    const list = product?.images || [];
    return list.map((src) => (src.startsWith('http') ? src : fullUrl(src)));
  }, [product]);

  const shop = product?.shop || {};
  const currency = shop.currency || 'USD';
  const variations = product?.variations || [];
  const current = variations[variant] || null;
  const price = current?.price ?? product?.sale_price ?? product?.price ?? 0;
  const listPrice = product?.price ?? 0;
  const hasSale = !current && product?.sale_price != null && product.sale_price < listPrice;
  const stock = current?.quantity ?? product?.quantity ?? 0;

  const add = () => {
    if (!product) return;
    // No account in this shop yet → offer Google (the API creates the customer)
    // instead of opening the drawer straight away.
    const needsAccount = !(isLoggedIn && customer?.shop_id === product.shop_id);
    addItem({ ...product, price, sale_price: null }, qty, current?.attrs || {}, {},
            { open: !needsAccount });
    if (needsAccount) askBuy({ id: product.shop_id, ...shop });
    else toast.success(t('addedToCart'));
  };

  const share = async () => {
    // The API turns this link into a preview **with the product picture**.
    const url = `${window.location.origin}/share/product/${product.id}`;
    const text = `${product.name} · ${price} ${currency}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: product.name, text, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success(t('linkCopied'));
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error(t('somethingWrong'));
    }
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-[1100px] px-3 lg:px-4 py-6 space-y-3">
        <div className="h-8 w-40 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="aspect-square rounded-2xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          <div className="space-y-3">
            <div className="h-6 w-3/4 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
            <div className="h-5 w-1/3 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
            <div className="h-24 rounded-2xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="mx-auto max-w-[1100px] px-3 lg:px-4 py-16 text-center">
        <p className="text-sm text-gray-400">{t('noProducts')}</p>
        <Link to="/shop" className="mt-3 inline-block text-sm font-bold" style={{ color: BLUE }}>
          ← {t('allProducts')}
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1100px] px-3 lg:px-4 py-4 space-y-4">
      <Link to="/shop" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:underline">
        <FiArrowLeft className="w-4 h-4" /> {t('allProducts')}
      </Link>

      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Gallery */}
        <div>
          <div className="aspect-square rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800 relative">
            {images.length > 0 ? (
              <img src={images[index]} alt={product.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-300">
                <FiShoppingBag className="w-16 h-16" />
              </div>
            )}
            {hasSale && (
              <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-red-500 text-white text-xs font-bold">
                {t('sale')}
              </span>
            )}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 text-white flex items-center justify-center"
                  aria-label="Previous image"
                >
                  <FiChevronLeft />
                </button>
                <button
                  type="button"
                  onClick={() => setIndex((i) => (i + 1) % images.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/45 text-white flex items-center justify-center"
                  aria-label="Next image"
                >
                  <FiChevronRight />
                </button>
              </>
            )}
          </div>

          {images.length > 1 && (
            <div className="mt-2 flex gap-2 overflow-x-auto no-scrollbar">
              {images.map((src, i) => (
                <button key={src + i} type="button" onClick={() => setIndex(i)}
                  className={`w-16 h-16 rounded-xl overflow-hidden border-2 shrink-0 ${
                    i === index ? 'border-[color:var(--primary)]' : 'border-transparent'
                  }`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="flex flex-col gap-3">
          <h1 className="text-xl font-extrabold leading-snug">{product.name}</h1>

          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-extrabold" style={{ color: BLUE }}>{price} {currency}</span>
            {hasSale && <span className="text-sm text-gray-400 line-through">{listPrice} {currency}</span>}
          </div>

          <div className="flex items-center gap-3 text-xs">
            <span className={stock > 0 ? 'text-emerald-600 font-bold' : 'text-red-500 font-bold'}>
              {stock > 0 ? `${t('inStock')} (${stock})` : t('outOfStock')}
            </span>
            {product.category?.name && (
              <Link to={`/shop?category=${encodeURIComponent(product.category.name)}`}
                className="inline-flex items-center gap-1 text-gray-500 hover:underline">
                <FiTag className="w-3.5 h-3.5" /> {product.category.name}
              </Link>
            )}
          </div>

          {/* The shop that sells it */}
          {shop.username && (
            <Link to={`/${shop.username}`}
              className="flex items-center gap-3 rounded-2xl border border-gray-100 dark:border-gray-800 p-3 hover:bg-gray-50 dark:hover:bg-gray-800">
              {shop.logo ? (
                <img src={fullUrl(shop.logo)} alt={shop.shop_name} className="w-10 h-10 rounded-xl object-cover" />
              ) : (
                <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-xs font-bold" style={{ background: BLUE }}>
                  {(shop.shop_name || shop.username).slice(0, 2).toUpperCase()}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block font-bold text-sm truncate">{shop.shop_name}</span>
                <span className="block text-[11px] text-gray-400">
                  @{shop.username} · {shop.product_count} {t('productsCount')}
                </span>
              </span>
              <span className="text-xs font-bold shrink-0" style={{ color: BLUE }}>{t('visitShop')} →</span>
            </Link>
          )}

          {/* The shop's own buttons — every social link the owner saved in the
              dashboard (Shop Settings → Social Media) + the shop contact, so the
              product page works like the shop's community detail page. */}
          {shop.username && (
            <ShopSocialLinks social={shop.social_media} contact={shop.contact} />
          )}

          {shop.username && shop.bio && (
            <p className="text-xs text-gray-500 dark:text-gray-400 whitespace-pre-line leading-relaxed">
              {shop.bio}
            </p>
          )}

          {/* Variations created by the shop owner */}
          {variations.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 mb-1.5">{t('productDetails')}</p>
              <div className="flex flex-wrap gap-1.5">
                {variations.map((v, i) => (
                  <button key={i} type="button" onClick={() => setVariant(i)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${
                      i === variant
                        ? 'text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                    }`}
                    style={i === variant ? { background: BLUE, borderColor: BLUE } : undefined}
                  >
                    {Object.values(v.attrs || {}).join(' / ') || `#${i + 1}`}
                    {v.price != null ? ` · ${v.price} ${currency}` : ''}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity + actions */}
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <div className="inline-flex items-center gap-2 rounded-xl border border-gray-200 dark:border-gray-700 px-2 py-1.5">
              <button type="button" onClick={() => setQty((v) => Math.max(1, v - 1))}
                className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center">
                <FiMinus className="w-3.5 h-3.5" />
              </button>
              <span className="w-8 text-center font-bold text-sm">{qty}</span>
              <button type="button" onClick={() => setQty((v) => v + 1)}
                className="w-7 h-7 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 flex items-center justify-center">
                <FiPlus className="w-3.5 h-3.5" />
              </button>
            </div>

            <button type="button" onClick={add} disabled={stock <= 0}
              className="flex-1 min-w-[150px] inline-flex items-center justify-center gap-2 py-3 rounded-xl text-white text-sm font-bold disabled:opacity-50"
              style={{ background: BLUE }}>
              <FiShoppingCart className="w-4 h-4" /> {t('addToCart')}
            </button>

            <button type="button" onClick={share} title={t('productShared')}
              className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800">
              <FiShare2 className="w-4 h-4" /> {t('share')}
            </button>
          </div>

          {product.description && (
            <div className="mt-1">
              <p className="text-xs font-semibold text-gray-500 mb-1">{t('productDetails')}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line leading-relaxed">
                {product.description}
              </p>
            </div>
          )}

          {(product.custom_attributes || []).length > 0 && (
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
              {product.custom_attributes.map((a, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-gray-400">{a.label || a.name}:</span>
                  <span className="font-semibold">{String(a.value ?? '')}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {(product.more_from_shop || []).length > 0 && (
        <section className="space-y-2">
          <h2 className="font-extrabold text-sm px-1">{t('moreFromShop')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3">
            {product.more_from_shop.map((p) => (
              <ShopProductCard key={p.id} product={{ ...p, shop }} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
